import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Search, Users, Mail, Phone,
  Filter, Grid, List, ChevronDown,
  UserCheck, AlertCircle, Loader2
} from 'lucide-react';
import { membersService } from '../services';
import type { FamilyMember } from '../types';
import { calculateAge } from '../utils/formatters';
import { getMemberDisplayName, getMemberInitials } from '../utils/name';
import { useImageUrl } from '../hooks/useImageUrl';
import { logger } from '../utils/logger';

interface MemberCardProps {
  member: FamilyMember;
  viewMode: 'grid' | 'list';
}

const MemberCard: React.FC<MemberCardProps> = ({ member, viewMode }) => {
  const resolvedImageUrl = useImageUrl(member.profileImage);

  const getRoleIcon = (linkedUserId?: string, isLinked?: boolean) => {
    if (!linkedUserId || !isLinked) return null;
    // In a real app, you'd get the user role from the linked user data
    // For now, we'll show a generic user icon for linked members
    return <UserCheck className="w-4 h-4 text-green-600" />;
  };

  if (viewMode === 'list') {
    return (
      <Link 
        to={`/members/${member.id}`}
        className="block bg-surface-1 rounded-lg border border-border p-4 hover:shadow-sm transition-all"
      >
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0">
            {resolvedImageUrl ? (
              <img
                src={resolvedImageUrl}
                alt={getMemberDisplayName(member)}
                className="w-16 h-16 rounded-full object-cover border border-border shadow-sm"
              />
            ) : (
              <div className="w-16 h-16 bg-brand rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm border border-border">
                {getMemberInitials(member)}
              </div>
            )}
            {getRoleIcon(member.linkedUserId, !!member.linkedUserId) && (
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1">
                {getRoleIcon(member.linkedUserId, !!member.linkedUserId)}
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-text-primary truncate">
                {getMemberDisplayName(member)}
              </h3>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-text-secondary mb-2">
              {member.generation && <span>Generation {member.generation}</span>}
              {member.branch && <span>{member.branch} Branch</span>}
              {member.birthDate && (
                <span>{calculateAge(member.birthDate)} years old</span>
              )}
            </div>
            
            {member.occupation && (
              <p className="text-text-tertiary text-sm mb-2 truncate">{member.occupation}</p>
            )}
            
            <div className="flex items-center gap-4 text-xs text-text-tertiary">
              {member.email && (
                <div className="flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  <span className="truncate">{member.email}</span>
                </div>
              )}
              {member.phone && (
                <div className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  <span>{member.phone}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex-shrink-0 text-text-tertiary">
            <ChevronDown className="w-5 h-5 rotate-[-90deg]" />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link 
      to={`/members/${member.id}`}
      className="block bg-surface-1 rounded-lg border border-border p-6 hover:shadow-sm transition-all"
    >
      <div className="text-center">
        <div className="relative inline-block mb-4">
          {resolvedImageUrl ? (
            <img
              src={resolvedImageUrl}
              alt={getMemberDisplayName(member)}
              className="w-20 h-20 rounded-full object-cover border border-border shadow-sm mx-auto"
            />
          ) : (
            <div className="w-20 h-20 bg-brand rounded-full flex items-center justify-center text-white font-bold text-xl shadow-sm border border-border">
              {getMemberInitials(member)}
            </div>
          )}
          {getRoleIcon(member.linkedUserId, !!member.linkedUserId) && (
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1">
              {getRoleIcon(member.linkedUserId, !!member.linkedUserId)}
            </div>
          )}
        </div>
        
        <div className="mb-2">
          <h3 className="text-lg font-semibold text-text-primary mb-1">
            {getMemberDisplayName(member)}
          </h3>
        </div>
        
        <div className="text-sm text-text-secondary mb-3">
          <div>Generation {member.generation}</div>
          <div>{member.branch} Branch</div>
          {member.birthDate && (
            <div>{calculateAge(member.birthDate)} years old</div>
          )}
        </div>
        
        {member.occupation && (
          <p className="text-text-tertiary text-sm mb-3 truncate">{member.occupation}</p>
        )}
        
        {(member.email || member.phone) && (
          <div className="flex justify-center gap-3 text-xs text-text-tertiary">
            {member.email && <Mail className="w-4 h-4" />}
            {member.phone && <Phone className="w-4 h-4" />}
          </div>
        )}
      </div>
    </Link>
  );
};

const MembersDirectoryPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedBranch, setSelectedBranch] = useState(searchParams.get('branch') || 'all');
  const [selectedGeneration, setSelectedGeneration] = useState(searchParams.get('generation') || 'all');
  const [includeTreeOnly, setIncludeTreeOnly] = useState(searchParams.get('tree') === 'true');


  // Load members data — re-fetch when includeTreeOnly changes
  useEffect(() => {
    const loadMembers = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await membersService.getFamilyMembers({
          limit: 1000,
          directoryOnly: !includeTreeOnly,
        });
        setMembers(response.members || []);
      } catch (err) {
        logger.error('Error loading members:', err);
        setError('Failed to load family members. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadMembers();
  }, [includeTreeOnly]);

  // Update URL params when filters change
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (searchTerm) newParams.set('search', searchTerm);
    if (selectedBranch !== 'all') newParams.set('branch', selectedBranch);
    if (selectedGeneration !== 'all') newParams.set('generation', selectedGeneration);
    if (includeTreeOnly) newParams.set('tree', 'true');

    setSearchParams(newParams, { replace: true });
  }, [searchTerm, selectedBranch, selectedGeneration, includeTreeOnly, setSearchParams]);

  // Get unique branches and generations for filters
  const { branches, generations } = useMemo(() => {
    const branchSet = new Set<string>();
    const generationSet = new Set<number>();
    
    members.forEach(member => {
      if (member.branch) branchSet.add(member.branch);
      if (member.generation) generationSet.add(member.generation);
    });
    
    return {
      branches: Array.from(branchSet).sort(),
      generations: Array.from(generationSet).sort((a, b) => a - b)
    };
  }, [members]);

  // Filter and sort members based on current filters
  const filteredMembers = useMemo(() => {
    return members.filter(member => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesName = `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchLower);
        const matchesNickname = member.nickname?.toLowerCase().includes(searchLower);
        const matchesOccupation = member.occupation?.toLowerCase().includes(searchLower);

        if (!matchesName && !matchesNickname && !matchesOccupation) {
          return false;
        }
      }

      // Branch filter
      if (selectedBranch !== 'all' && member.branch !== selectedBranch) {
        return false;
      }

      // Generation filter
      if (selectedGeneration !== 'all' && member.generation?.toString() !== selectedGeneration) {
        return false;
      }

      return true;
    }).sort((a, b) => {
      // Sort by last name, then by first name
      const lastNameCompare = a.lastName.localeCompare(b.lastName);
      if (lastNameCompare !== 0) return lastNameCompare;
      return a.firstName.localeCompare(b.firstName);
    });
  }, [members, searchTerm, selectedBranch, selectedGeneration]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBranch('all');
    setSelectedGeneration('all');
    setIncludeTreeOnly(false);
  };

  if (loading) {
    return (
      <div>
        <div className="pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-brand mx-auto mb-4" />
                <p className="text-text-primary text-lg font-medium">Loading family members...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Members</h2>
                <p className="text-red-600 mb-4">{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="bg-brand hover:bg-brand-dark text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-text-primary">
      
      <div className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">Family Directory</h1>
                <p className="text-text-secondary">
                  {filteredMembers.length} of {members.length} family members
                  {!includeTreeOnly && ' with contact info'}
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                  className="p-2 bg-surface-1 hover:bg-surface-2 rounded-lg border border-border text-text-tertiary transition-colors"
                  title={`Switch to ${viewMode === 'grid' ? 'list' : 'grid'} view`}
                >
                  {viewMode === 'grid' ? <List className="w-5 h-5" /> : <Grid className="w-5 h-5" />}
                </button>
                
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    showFilters 
                      ? 'bg-brand text-white' 
                      : 'bg-surface-1 hover:bg-surface-2 text-text-secondary border border-border'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {(selectedBranch !== 'all' || selectedGeneration !== 'all' || includeTreeOnly) && (
                    <span className="bg-surface-2 text-text-primary text-xs px-2 py-1 rounded-full">
                      Active
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-tertiary w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, nickname, or occupation..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-surface-1 border border-border rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent text-text-primary placeholder-text-tertiary"
              />
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-surface-1 rounded-lg border border-border p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary">Filters</h3>
                <button
                  onClick={clearFilters}
                  className="text-text-tertiary hover:text-text-primary text-sm font-medium transition-colors"
                >
                  Clear All
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Branch</label>
                  <select
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent bg-white"
                  >
                    <option value="all">All Branches</option>
                    {branches.map(branch => (
                      <option key={branch} value={branch}>{branch} Branch</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Generation</label>
                  <select
                    value={selectedGeneration}
                    onChange={(e) => setSelectedGeneration(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent bg-white"
                  >
                    <option value="all">All Generations</option>
                    {generations.map(generation => (
                      <option key={generation} value={generation}>Generation {generation}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-end">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={includeTreeOnly}
                      onChange={(e) => setIncludeTreeOnly(e.target.checked)}
                      className="mr-2 rounded border-border text-brand focus:ring-brand"
                    />
                    <span className="text-sm font-medium text-text-secondary">
                      Include tree-only members
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Members Grid/List */}
          {filteredMembers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-text-primary mb-2">No members found</h3>
              <p className="text-text-secondary">
                {searchTerm || selectedBranch !== 'all' || selectedGeneration !== 'all' || includeTreeOnly
                  ? 'Try adjusting your search or filters'
                  : 'No family members have been added yet'}
              </p>
              {(searchTerm || selectedBranch !== 'all' || selectedGeneration !== 'all' || includeTreeOnly) && (
                <button
                  onClick={clearFilters}
                  className="mt-4 bg-brand hover:bg-brand-dark text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'space-y-4'
            }>
              {filteredMembers.map((member) => (
                <MemberCard
                  key={member.id}
                  member={member}
                  viewMode={viewMode}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembersDirectoryPage;
